import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { redirectToLogin } from "@/lib/auth";
import { CreditCard, Zap, Globe, Calendar, Shield, Smartphone, ArrowRight } from "lucide-react";

export default function Home() {
  const { user, loading } = useAuth();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 inset-x-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="container flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-pink-500 flex items-center justify-center">
              <span className="text-white font-bold text-sm">CB</span>
            </div>
            <span className="font-bold text-lg text-foreground">card.buzz</span>
          </Link>
          <div className="flex items-center gap-3">
            {loading ? null : user ? (
              <Link to={user.role === "admin" ? "/admin" : "/dashboard"} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity">
                {user.role === "admin" ? "Admin Panel" : user.role === "secretary" ? "Secretary Panel" : "My Dashboard"}
              </Link>
            ) : (
              <button onClick={() => redirectToLogin("/")} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity">
                Sign In
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-pink-500/5" />
        <div className="container relative">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
              <Zap className="w-3.5 h-3.5" />
              Now with Calendar Integration
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-foreground leading-tight tracking-tight">
              Your Digital Identity,{" "}
              <span className="bg-gradient-to-r from-primary to-pink-500 bg-clip-text text-transparent">
                Reimagined
              </span>
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Create stunning digital business cards with NFC tap, QR codes, and calendar scheduling.
              Share your professional identity instantly — no app required.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => user ? window.location.href = (user.role === "admin" ? "/admin" : "/dashboard") : redirectToLogin("/")}
                className="px-8 py-3.5 rounded-xl bg-primary text-primary-foreground font-semibold text-base hover:opacity-90 transition-opacity flex items-center gap-2 shadow-lg shadow-primary/25"
              >
                Get Started <ArrowRight className="w-4 h-4" />
              </button>
              <Link to="/u/demo" className="px-8 py-3.5 rounded-xl border border-border text-foreground font-medium text-base hover:bg-accent transition-colors">
                View Demo Card
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-muted/30">
        <div className="container">
          <h2 className="text-3xl font-bold text-center text-foreground mb-4">Everything You Need</h2>
          <p className="text-center text-muted-foreground mb-12 max-w-xl mx-auto">
            A complete platform for digital business cards and professional scheduling.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: CreditCard, title: "Digital Cards", desc: "Beautiful, responsive digital business cards with multilingual support." },
              { icon: Smartphone, title: "NFC & QR", desc: "Tap to share with NFC or scan a QR code. No app download needed." },
              { icon: Globe, title: "PWA Wallet", desc: "Add cards to your phone's home screen as a progressive web app." },
              { icon: Calendar, title: "Calendar Sync", desc: "Aggregate calendars from Google, Microsoft, Lark, and iCloud." },
              { icon: Shield, title: "Privacy Controls", desc: "Share calendars with visibility masking — busy/free or full details." },
              { icon: Zap, title: "Booking Engine", desc: "Let contacts book meetings based on your real-time availability." },
            ].map((f) => (
              <div key={f.title} className="bg-card rounded-xl p-6 border border-border hover:shadow-md transition-shadow">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <f.icon className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">{f.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-border">
        <div className="container flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-gradient-to-br from-primary to-pink-500 flex items-center justify-center">
              <span className="text-white font-bold text-[10px]">CB</span>
            </div>
            <span className="text-sm text-muted-foreground">&copy; {new Date().getFullYear()} card.buzz</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-muted-foreground">
            <a href="mailto:hello@card.buzz" className="hover:text-foreground transition-colors">Contact</a>
            {user?.role === "admin" && (
              <Link to="/admin" className="hover:text-foreground transition-colors">Admin</Link>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
}
